import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspaceFilters } from "@presentation/hooks/useWorkspaceFilters";
import { settingsUrl } from "@presentation/common/settingsRoutes";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn, GridCustomCellProps } from "@progress/kendo-react-grid";
import { gearIcon, gridIcon, pencilIcon, plusOutlineIcon, trashIcon } from "@progress/kendo-svg-icons";
import { useShallow } from "zustand/react/shallow";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamProjectActionsService } from "@application/useCases/workspace/TeamProjectActionsService";
import { useProjectPermission, useTeamPermission, useTeamPermissions } from "@presentation/components/helpers/usePermissionCheck";
import { GatedActionButton } from "@presentation/components/permission/GatedActionButton";
import { CreateProjectDialog } from "@presentation/components/team/CreateProjectDialog";
import { RenameProjectDialog } from "@presentation/components/project/RenameProjectDialog";
import { DeleteProjectDialog } from "@presentation/components/project/DeleteProjectDialog";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import { fillCardStyle, mutedTextStyle, pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle, tableRowActionsStyle } from "@presentation/common/pageLayout";
import { ProjectActions, ProjectRoles, TeamActions } from "@domain/schemas/workspace";
import type { MyProject } from "@domain/schemas/workspace";
import { EntitlementLockedBadge } from "@presentation/components/permission/EntitlementLockedBadge";
import { useIsEntitled } from "@presentation/hooks/useEntitlements";
import { CONTEXT_MANAGEMENT_ENTITLEMENT_NAME } from "@domain/valueObjects/contextManagementEntitlement";
import { DefaultContextBadge } from "@presentation/components/workspace/DefaultContextBadge";

// `role` is widened from `ProjectRoleName | null` to `string` - purely a display label here, and
// "-" (see `rows` below) isn't itself a `ProjectRoleName` value.
type ProjectRow = Omit<MyProject, "role"> & { role: string; teamName: string };

type DialogState = { kind: "none" } | { kind: "createProject" } | { kind: "renameProject"; project: ProjectRow } | { kind: "deleteProject"; project: ProjectRow };

/**
Job setup, rename, and delete act on the project's own owning team, which may differ per row in this flat,
cross-team table - the permission check must be made per row, hence this own small component
(hooks can't be called from a cell callback). Renders just its action buttons (no `<td>`/wrapper of
its own) so the parent cell can lay out every action button - these included - in one
consistently-spaced row.
*/
const ProjectCreateJobAction: React.FC<{ project: ProjectRow; onCreateJob: () => void }> = ({ project, onCreateJob }) => {
  const triggerJobPermission = useProjectPermission(project.id, ProjectActions.TriggerJob, project.isDefault);
  return <GatedActionButton type="button" fillMode="flat" svgIcon={plusOutlineIcon} title="Set up a job" aria-label="Set up a job" permission={triggerJobPermission} onClick={onCreateJob} />;
};

const ProjectRowActions: React.FC<{ project: ProjectRow; onRename: () => void; onDelete: () => void }> = ({ project, onRename, onDelete }) => {
  const renameProjectPermission = useTeamPermission(project.teamId, TeamActions.RenameProject, project.isDefault);
  const deleteProjectPermission = useTeamPermission(project.teamId, TeamActions.DeleteProject, project.isDefault);

  return (
    <>
      <GatedActionButton type="button" fillMode="flat" svgIcon={pencilIcon} title="Rename" aria-label="Rename" permission={renameProjectPermission} onClick={onRename} />
      <GatedActionButton type="button" fillMode="flat" themeColor="error" svgIcon={trashIcon} title="Delete" aria-label="Delete" permission={deleteProjectPermission} onClick={onDelete} />
    </>
  );
};

/**
Union of every project the user holds a project-level role in, across every owning team (`/projects`) -
a flat, project-level table. The team breadcrumb at the top (Sidebar) drives the `?teamId=` filter;
"All teams" (no param) shows every project regardless of owning team. The creation dialog offers
only teams where the caller has permission to create a project.
*/
const ProjectsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { teams, projects } = useWorkspaceContextStore(useShallow((state) => ({ teams: state.teams, projects: state.projects })));
  const setActiveTeamContext = useWorkspaceContextStore((state) => state.setActiveTeamContext);
  const setWorkspace = useWorkspaceContextStore((state) => state.setWorkspace);
  const teamProjectActionsService = useInjection(TeamProjectActionsService);
  const contextManagementEntitled = useIsEntitled(CONTEXT_MANAGEMENT_ENTITLEMENT_NAME);
  const [searchTerm, setSearchTerm] = useState("");
  const filters = useWorkspaceFilters();
  const scopeTeamId = filters.teamId;
  const [dialog, setDialog] = useState<DialogState>({ kind: "none" });

  const teamPermissions = useTeamPermissions(teams, TeamActions.CreateProject);
  const eligibleTeams = teamPermissions.filter(({ permission }) => permission.allowed).map(({ team }) => team);
  const createProjectPermission = scopeTeamId
    ? teamPermissions.find(({ team }) => team.id === scopeTeamId)?.permission
    : (teamPermissions.find(({ permission }) => permission.allowed)?.permission ?? teamPermissions.find(({ permission }) => permission.reason === "entitlement")?.permission);

  const managerUserNames = useUserDisplayNames(React.useMemo(() => projects.flatMap((project) => project.managerUserIds ?? []), [projects]));

  const teamNameById = React.useMemo(() => new Map(teams.map((team) => [team.id, team.name])), [teams]);

  const rows = React.useMemo<ProjectRow[]>(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();
    return (
      projects
        .filter((project) => !scopeTeamId || project.teamId === scopeTeamId)
        .filter((project) => filters.scope !== "project" || !filters.projectId || project.id === filters.projectId)
        .filter((project) => !normalizedTerm || project.name.toLowerCase().includes(normalizedTerm))
        // Only an Admin can see a project with no role of their own (every other caller's "my
        // projects" rows always have one) - "-" marks that rather than fabricating a role.
        .map((project) => ({ ...project, role: project.role ?? "-", teamName: teamNameById.get(project.teamId) ?? project.teamId }))
        .toSorted((left, right) => left.name.localeCompare(right.name))
    );
  }, [projects, scopeTeamId, searchTerm, teamNameById, filters.scope, filters.projectId]);

  const deleteProject = (project: ProjectRow) => {
    setWorkspace(
      teams,
      projects.filter((candidate) => candidate.id !== project.id),
    );
  };

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>Projects</h1>
          {createProjectPermission && (
            <GatedActionButton type="button" themeColor="primary" permission={createProjectPermission} onClick={() => setDialog({ kind: "createProject" })}>
              Create project
            </GatedActionButton>
          )}
        </div>
      </div>

      <div style={pageFillContentStyle}>
        <Card style={fillCardStyle}>
          <CardBody style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", padding: 0 }}>
            <input
              type="search"
              className="project-sidebar-search"
              placeholder="Search projects"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              style={{ minWidth: 220, flex: "0 0 auto", margin: "16px 16px 12px" }}
            />

            {rows.length === 0 ? (
              <p style={mutedTextStyle}>No projects match.</p>
            ) : (
              <Grid style={{ flex: "1 1 auto", minHeight: 0 }} data={rows} dataItemKey="id" sortable={true}>
                <GridColumn field="teamName" title="Team" width="220px" />
                <GridColumn
                  field="name"
                  title="Project"
                  cell={(properties: GridCustomCellProps) => (
                    <td>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        {properties.dataItem.name}
                        {properties.dataItem.isDefault && <DefaultContextBadge context="project" />}
                        {!properties.dataItem.isDefault && contextManagementEntitled === false && <EntitlementLockedBadge />}
                      </span>
                    </td>
                  )}
                />
                <GridColumn field="role" title="Your role" width="140px" />
                <GridColumn
                  title="Project lead"
                  cell={(properties: GridCustomCellProps) => {
                    const project = properties.dataItem as ProjectRow;
                    return (
                      <td>{project.managerUserIds ? project.managerUserIds.map((userId) => formatUserDisplayName(managerUserNames[userId], userId)).join(", ") || "No manager assigned" : "-"}</td>
                    );
                  }}
                />
                <GridColumn
                  title=""
                  width="230px"
                  cell={(properties: GridCustomCellProps) => {
                    const project = properties.dataItem as ProjectRow;
                    return (
                      <td>
                        <div style={tableRowActionsStyle}>
                          <ProjectCreateJobAction
                            project={project}
                            onCreateJob={() => {
                              setActiveTeamContext(project.teamId, project.id);
                              navigate("/job-setup");
                            }}
                          />
                          <Button
                            type="button"
                            fillMode="flat"
                            svgIcon={gridIcon}
                            title="View jobs"
                            aria-label="View jobs"
                            onClick={() => navigate(`/jobs?teamId=${project.teamId}&projectId=${project.id}`)}
                          />
                          <Button
                            type="button"
                            fillMode="flat"
                            svgIcon={gearIcon}
                            title="Project settings"
                            aria-label="Project settings"
                            onClick={() => navigate(settingsUrl({ level: "project", projectId: project.id }))}
                          />
                          <ProjectRowActions project={project} onRename={() => setDialog({ kind: "renameProject", project })} onDelete={() => setDialog({ kind: "deleteProject", project })} />
                        </div>
                      </td>
                    );
                  }}
                />
              </Grid>
            )}
          </CardBody>
        </Card>
      </div>

      {dialog.kind === "createProject" && (
        <CreateProjectDialog
          teams={eligibleTeams}
          initialTeamId={scopeTeamId}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={(created) => {
            setWorkspace(teams, [
              ...projects,
              {
                id: created.id,
                name: created.name,
                teamId: created.teamId,
                isDefault: created.isDefault,
                role: null,
                managerUserIds: created.memberships.filter((membership) => membership.role === ProjectRoles.Manager).map((membership) => membership.userId),
              },
            ]);
          }}
        />
      )}
      {dialog.kind === "renameProject" && (
        <RenameProjectDialog
          currentName={dialog.project.name}
          onRename={(newName) => teamProjectActionsService.renameProjectAsync(dialog.project.id, newName)}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={(updated) =>
            setWorkspace(
              teams,
              projects.map((candidate) => (candidate.id === updated.id ? { ...candidate, name: updated.name } : candidate)),
            )
          }
        />
      )}
      {dialog.kind === "deleteProject" && (
        <DeleteProjectDialog
          teamId={dialog.project.teamId}
          projectId={dialog.project.id}
          projectName={dialog.project.name}
          onClose={() => setDialog({ kind: "none" })}
          onSuccess={() => deleteProject(dialog.project)}
        />
      )}
    </div>
  );
};

export default ProjectsListPage;
